/**
 * GestureHands — reusable MediaPipe HandLandmarker loader.
 *
 * Extracted from the proven /rock-paper-scissors loader so any tool that
 * needs hand landmarks (air-draw, future whiteboard/presenter overlays)
 * shares one CSP-clean, locale-aware code path instead of re-implementing it.
 *
 * Why MediaPipe and not OpenCV: the site CSP allows `wasm-unsafe-eval` but
 * NOT plain `unsafe-eval`. MediaPipe Tasks-Vision is pure WebAssembly →
 * works on the main thread. OpenCV.js builds functions via `new Function`
 * → blocked. (See memory reference_browser_cv_csp_eval_2026_06_24.)
 *
 * Locale routing: RU users hit the same-origin nginx proxies first
 * (/cdn/jsdelivr for the lib, /cdn/gstorage for the model) so nothing
 * loads from a foreign CDN (RKN-safe); everyone else hits the CDN first
 * with the proxy as fallback. The /cdn/* proxies are PROD-only (Beget
 * nginx) — on local Herd non-RU locales reach the direct CDN (fine), and
 * RU locally falls through to direct too.
 *
 * Public API (window.GestureHands):
 *   load(opts) -> Promise<HandLandmarker>
 *     opts.numHands     (default 1)
 *     opts.minHandDetectionConfidence (default 0.5)
 *     opts.minTrackingConfidence      (default 0.5)
 *   close() — free the cached landmarker + module.
 *
 * The returned landmarker runs in `VIDEO` mode: call
 * `landmarker.detectForVideo(videoEl, performance.now())` per frame.
 */
(function () {
    'use strict';

    var TASKS_VISION_VERSION = '0.10.14';
    var MODEL_PATH = 'hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task';

    function urls() {
        var locale = (typeof window !== 'undefined' && window.__LOCALE)
            ? String(window.__LOCALE).toLowerCase() : 'en';
        var origin = (typeof window !== 'undefined' && window.location)
            ? window.location.origin : '';
        var libProxy  = origin + '/cdn/jsdelivr/npm/@mediapipe/tasks-vision@' + TASKS_VISION_VERSION;
        var libDirect = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@' + TASKS_VISION_VERSION;
        var mProxy  = origin + '/cdn/gstorage/mediapipe-models/' + MODEL_PATH;
        var mDirect = 'https://storage.googleapis.com/mediapipe-models/' + MODEL_PATH;
        return locale === 'ru'
            ? { libs: [libProxy, libDirect], model: [mProxy, mDirect] }
            : { libs: [libDirect, libProxy], model: [mDirect, mProxy] };
    }

    var modCache = null, landmarkerCache = null, loading = null;

    // Decide whether the GPU (WebGL) delegate is worth trying first. On real
    // devices a hardware GL context makes the GPU delegate ~4-5× faster than CPU
    // (15-20ms vs 80ms detection). But where the only GL is a SOFTWARE rasterizer
    // (SwiftShader / llvmpipe — headless CI, many VMs, some locked-down Linux),
    // the GPU delegate is SLOWER than the CPU XNNPACK path and would starve the
    // main thread. Probe the renderer and order the delegates accordingly.
    function gpuLikelyHardware() {
        // One rule for every MediaPipe tool lives in the shared door (timbrica-core.js).
        if (window.__mpSoftFit && window.__mpSoftFit.gpuFirst) return window.__mpSoftFit.gpuFirst();
        try {
            if (typeof document === 'undefined') return false;
            var c = document.createElement('canvas');
            var gl = c.getContext('webgl2') || c.getContext('webgl');
            if (!gl) return false;
            var ext = gl.getExtension('WEBGL_debug_renderer_info');
            var r = ext ? String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) || '') : '';
            var lose = gl.getExtension('WEBGL_lose_context'); if (lose) lose.loseContext();
            if (!r) return true; // renderer hidden (privacy) → assume HW; CPU fallback covers us
            return !/swiftshader|llvmpipe|software|basic render|microsoft basic/i.test(r);
        } catch (e) { return true; }
    }

    function load(opts) {
        opts = opts || {};
        if (landmarkerCache) return Promise.resolve(landmarkerCache);
        if (loading) return loading;

        loading = (async function () {
            var u = urls(), mod = null, libBase = null, lastErr = null;

            // Import the vision bundle — try each candidate URL in order.
            for (var i = 0; i < u.libs.length; i++) {
                try {
                    mod = await import(u.libs[i] + '/vision_bundle.mjs');
                    libBase = u.libs[i];
                    break;
                } catch (e) { lastErr = e; }
            }
            if (!mod) throw new Error('mp-lib: ' + (lastErr && lastErr.message));
            modCache = mod;

            // The WASM fileset MUST come from the SAME origin as the imported
            // lib, or the runtime's shared-state plumbing fails silently.
            var fileset = await mod.FilesetResolver.forVisionTasks(libBase + '/wasm');

            // Prefer the GPU (WebGL) delegate: it drops detection latency from
            // ~80ms (CPU) to ~15-20ms, which is the difference between a cursor
            // that lags ~3 frames behind the fingertip (and drops detections on
            // fast motion → broken strokes) and one that tracks in real time.
            // GPU is Chrome-family only; Safari/unsupported devices throw on
            // init → fall back to CPU so the tool still works everywhere.
            var delegates = gpuLikelyHardware() ? ['GPU', 'CPU'] : ['CPU', 'GPU'];
            for (var d = 0; d < delegates.length; d++) {
                for (var j = 0; j < u.model.length; j++) {
                    try {
                        var hlOpts = {
                            baseOptions: { modelAssetPath: u.model[j], delegate: delegates[d] },
                            runningMode: 'VIDEO',
                            numHands: opts.numHands != null ? opts.numHands : 1,
                            minHandDetectionConfidence: opts.minHandDetectionConfidence != null ? opts.minHandDetectionConfidence : 0.5,
                            minHandPresenceConfidence: opts.minHandPresenceConfidence != null ? opts.minHandPresenceConfidence : 0.5,
                            minTrackingConfidence: opts.minTrackingConfidence != null ? opts.minTrackingConfidence : 0.5,
                        };
                        // Без WebGL2 — CPU и программный «WebGL2» (timbrica-core.js, __mpSoftFit).
                        if (window.__mpSoftFit) await window.__mpSoftFit(hlOpts);
                        var hl = await mod.HandLandmarker.createFromOptions(fileset, hlOpts);
                        // "Created" is not "works": a GPU graph comes up on the first frame,
                        // and a crippled WebGL2 dies there — past this ladder, in the tool's
                        // loop, where every frame then silently finds no hand. One real
                        // frame decides; a failed delegate is not retried on the next URL.
                        if (window.__mpSoftFit && window.__mpSoftFit.probe && !window.__mpSoftFit.probe(hl, 'VIDEO')) {
                            lastErr = new Error('probe failed on ' + hlOpts.baseOptions.delegate);
                            break;
                        }
                        landmarkerCache = hl;
                        landmarkerCache.__gdDelegate = hlOpts.baseOptions.delegate;
                        return landmarkerCache;
                    } catch (e) { lastErr = e; }
                }
            }
            throw new Error('mp-model: ' + (lastErr && lastErr.message));
        })();

        loading.catch(function () { loading = null; }); // allow retry after failure
        return loading;
    }

    function close() {
        try { if (landmarkerCache) landmarkerCache.close(); } catch (_) {}
        landmarkerCache = null;
        modCache = null;
        loading = null;
    }

    window.GestureHands = { load: load, close: close, VERSION: TASKS_VISION_VERSION };
})();
